import { Box, Button, Divider, Drawer } from "@material-ui/core";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";
import Analyze from "src/icons/Analyze";
import Block from "src/icons/Block";
import Location from "src/icons/Location";
import Map from "src/icons/Map";
import Pin from "src/icons/Pin";
import Search from "src/icons/Search";
import beta from "../../../src/icons/beta-button-50.png";
import ChartPieIcon from "../../icons/ChartPie";
import ChartSquareBarIcon from "../../icons/ChartSquareBar";
import ClipboardListIcon from "../../icons/ClipboardList";
import Home from "../../icons/Home";
import UserIcon from "../../icons/User";
import newIcon from "../../icons/icons8-new-stamp-50.png";
import { hasPermission } from "../../utils/handlePermissions";
import NavItem from "../NavItem";
import NavSection from "../NavSection";
import Scrollbar from "../Scrollbar";

const sections = [
  {
    items: [
      {
        title: "Dashboard",
        path: "/dashboard/main",
        icon: <Home fontSize="small" />,
      },
    ],
  },
  {
    title: "Search",
    items: [
      {
        title: "Overview",
        path: "/dashboard/overview",
        icon: <ChartPieIcon fontSize="small" />,
      },
      {
        title: "Analytics",
        path: "/dashboard/data-exploration",
        icon: <Analyze fontSize="small" />,
      },
      {
        title: "Indices",
        path: "/dashboard/indices",
        icon: <ClipboardListIcon fontSize="small" />,
      },
      {
        title: "Monitoring",
        path: "/dashboard/monitoring",
        icon: <ChartSquareBarIcon fontSize="small" />,
      },
      {
        title: "Trends",
        path: "/dashboard/",
        icon: <Search fontSize="small" />,
        info: <img src={newIcon} alt="New" style={{ height: 35 }} />,
        children: [
          {
            title: "Keyword Trends",
            path: "analytics/keywords",
            info: <img src={newIcon} alt="New" style={{ height: 35 }} />,
          },
          {
            title: "Search Trends",
            path: "analytics/search",
            info: <img src={newIcon} alt="New" style={{ height: 35 }} />,
          },
        ],
      },
    ],
  },

  {
    title: "Analytics",
    items: [
      {
        title: "Overview",
        path: "/dashboard/analytics",
        icon: <ChartPieIcon fontSize="small" />,
      },
      {
        title: "Destination",
        path: "/analytics/destination/overview",
        icon: <Location fontSize="small" />,
        children: [
          {
            title: "Destination by country",
            path: "analytics/destination/country",
          },
          {
            title: "Destination by prices",
            path: "analytics/destination/prices",
          },
          {
            title: "Destination by departure dates",
            path: "analytics/destination/departure-dates",
          },
          {
            title: "Destination comparison",
            path: "analytics/destination/comparison",
          },
        ],
      },
      {
        title: "Offer",
        path: "/analytics/offer/overview",
        icon: <Map fontSize="small" />,
        children: [
          {
            title: "Offer by country",
            path: "analytics/offer/country",
          },
          {
            title: "Offer by departure dates",
            path: "analytics/offer/departure-dates",
          },
          {
            title: "Offer comparison",
            path: "analytics/offer/comparison",
          },
        ],
      },
    ],
  },
  {
    title: "Merchandising",
    items: [
      {
        title: "Overview",
        path: "merchandising/",
        icon: <ChartPieIcon fontSize="small" />,
      },
      {
        title: "SearchPin",
        path: "merchandising/search",
        icon: <Pin fontSize="small" />,
        info: <img src={beta} alt="Beta" style={{ height: 40 }} />,
      },
      {
        title: "OffersBlock",
        path: "merchandising/offers",
        icon: <Block fontSize="small" />,
        info: <img src={beta} alt="Beta" style={{ height: 40 }} />,
      },
    ],
  },

  {
    title: "Management",
    items: [
      {
        title: "Account",
        path: "/dashboard/account",
        icon: <UserIcon fontSize="small" />,
      },
      /*
    {
      title: "Invoices",
      path: "/dashboard/invoices",
      icon: <ReceiptIcon fontSize="small" />,
      children: [
        {
          title: "List",
          path: "/dashboard/invoices",
        },
        {
          title: "Details",
          path: "/dashboard/invoices/1",
        },
      ],
    },
     */
    ],
  },
];
const PrivacyOrTerms = [
  {
    title: "Privacy policy",
    path: "/dashboard/privacy-policy",
    icon: null,
    info: "",
  },
  {
    title: "Terms of service",
    path: "/dashboard/terms-of-service",
    icon: null,
    info: "",
  },
];

const changelogElement = {
  title: "Changelog",
  path: "/dashboard/changelog",
  icon: null,
  info: "",
};
const changelogDepth = 0;
const changelogKey = `${changelogElement.title}-${changelogDepth}`;
const DashboardSidebar = (props) => {
  const { t } = useTranslation();

  const { onMobileClose, openMobile } = props;
  const location = useLocation();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  const exactMatch = (el) =>
    el.path
      ? !!matchPath(
          {
            path: el.path,
            end: true,
          },
          location.pathname
        )
      : false;

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Scrollbar options={{ suppressScrollX: true }}>
        <Box display={{ xs: "none", lg: "block" }}></Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          {sections.map((section) => {
            if (
              section.title === "Analytics" &&
              !hasPermission("dashboard_analytics")
            ) {
              return null;
            }
            if (
              section.title === "Management" &&
              !hasPermission("dashboard_management")
            ) {
              return null;
            }

            const filteredItems = section.items.filter((item) => {
              if (
                section.title == "Merchandising" &&
                item.title === "Overview" &&
                !(
                  hasPermission("dashboard_rules_search") ||
                  hasPermission("dashboard_rules_offer")
                )
              ) {
                return false;
              }
              if (
                item.title === "Trends" &&
                !(
                  hasPermission("dashboard_analytics_keyword_trends") ||
                  hasPermission("dashboard_analytics_search_trends")
                )
              ) {
                return null;
              }
              if (
                item.title === "SearchPin" &&
                !hasPermission("dashboard_rules_search")
              ) {
                return false;
              }
              if (
                item.title === "OffersBlock" &&
                !hasPermission("dashboard_rules_offer")
              ) {
                return false;
              }
              if (
                item.title === "Destination" &&
                !hasPermission("dashboard_analytics_destination")
              ) {
                return false;
              }
              if (
                item.title === "Offer" &&
                !hasPermission("dashboard_analytics_offer")
              ) {
                return false;
              }
              if (
                section.title == "Analytics" &&
                item.title === "Overview" &&
                !hasPermission("dashboard_analytics_overview")
              ) {
                return false;
              }
              return true;
            });

            if (filteredItems.length === 0) {
              return null;
            }
            const sectionData = {
              ...section,
              title: section.title ? t(`title.${section.title}`) : "",
            };

            return (
              <NavSection
                key={section.title}
                pathname={location.pathname}
                sx={{
                  "& + &": {
                    mt: 1,
                  },
                }}
                {...sectionData}
                items={filteredItems}
              />
            );
          })}
        </Box>

        <Divider />

        {hasPermission("dashboard_button_playground") && (
          <Button
            color="primary"
            variant="outlined"
            sx={{ my: "25px", mx: "20px" }}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_PLAYGROUND_URL}/?session=init`
              )
            }
          >
            API Playground
          </Button>
        )}

        {hasPermission("dashboard_button_demo") && (
          <Button
            color="primary"
            variant="outlined"
            sx={{ mx: "20px" }}
            onClick={() =>
              window.open(`${process.env.REACT_APP_DEMO_URL}/?session=init`)
            }
          >
            Demo Search
          </Button>
        )}
        <Button
          color="primary"
          variant="outlined"
          sx={{ my: "25px", mx: "20px" }}
          onClick={() =>
            window.open(process.env.REACT_APP_TECHNICAL_DOCUMENTATION)
          }
        >
          {t(`title.technical_documentation`)}
        </Button>
      </Scrollbar>

      <Box p={2}>
        {hasPermission("dashboard_changelog") && (
          <NavItem
            active={exactMatch(changelogElement)}
            depth={changelogDepth}
            icon={changelogElement.icon}
            info={changelogElement.info}
            key={changelogKey}
            path={changelogElement.path}
            title={t(`title.${changelogElement.title}`)}
          ></NavItem>
        )}

        {hasPermission("dashboard_terms_of_service_privacy_policy") &&
          PrivacyOrTerms.map((el) => {
            return (
              <NavItem
                active={exactMatch(el)}
                depth={changelogDepth}
                icon={el.icon}
                info={el.info}
                key={`PrivacyOfTerms-${el.title}`}
                path={el.path}
                title={t(`title.${el.title}`)}
              ></NavItem>
            );
          })}
      </Box>
    </Box>
  );

  return (
    <>
      <Box display={{ xs: "none", lg: "block" }}>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              width: 280,
            },
          }}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Box>
      <Box display={{ xs: "none", lg: "block" }}>
        <Drawer
          anchor="left"
          open
          PaperProps={{
            sx: {
              backgroundColor: "background.paper",
              height: "calc(100% - 64px) !important",
              top: "64px !Important",
              width: 280,
            },
          }}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Box>
    </>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default DashboardSidebar;
